import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Flexbox } from '@ibinder/common-frontend';
import AdminModal from '../AdminModal';
import GenericModalWrapper from '../../../../../../components/GlobalTemplates/GenericModalWrapper';

const StatusRow = styled(Flexbox)`
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const StatusLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
`;

type MfaLevel = 'required' | 'optional' | 'off';

const badgeBackground = (level: MfaLevel, theme: any) => {
  if (level === 'required') return theme.colors.success;
  if (level === 'optional') return theme.colors.warning ?? '#f59e0b';
  return theme.colors.error;
};

const StatusBadge = styled.span<{ level: MfaLevel }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${({ level, theme }) => badgeBackground(level, theme)};
  color: #fff;
`;

const WarningBox = styled.div`
  background: #fff3e0;
  border: 2px solid #e65100;
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;
`;

const WarningTitle = styled.p`
  color: #b71c1c;
  font-weight: 700;
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
`;

const WarningText = styled.p`
  color: #4a1c1c;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
`;

const AccessDeniedBox = styled.div`
  background: #fce4ec;
  border: 2px solid #b71c1c;
  border-radius: 4px;
  padding: 1rem;
`;

const AccessDeniedTitle = styled.p`
  color: #b71c1c;
  font-weight: 700;
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
`;

const AccessDeniedText = styled.p`
  color: #4a1c1c;
  font-size: 0.9rem;
  margin: 0;
`;

const ButtonRow = styled(Flexbox)`
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const RemoveSection = styled.div`
  margin-top: 1rem;
`;

type Props = {
  mfaLevel: MfaLevel;
  loading: boolean;
  userName: string;
  accessDenied: boolean;
  onClose: () => void;
  onRemove2FA: () => void;
  removingMfa: boolean;
};

const UserMfaModal = React.forwardRef<HTMLDivElement, Props>(
  (
    { mfaLevel, loading, userName, accessDenied, onClose, onRemove2FA, removingMfa },
    ref
  ) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleConfirmRemove = () => {
      onRemove2FA();
      setShowConfirm(false);
    };

    const badgeLabel =
      mfaLevel === 'required' ? 'Required' : mfaLevel === 'optional' ? 'Optional' : 'Off';

    return (
      <AdminModal
        ref={ref}
        label={`MFA status for ${userName}`}
        name="User MFA viewer"
        onClose={onClose}
        loading={loading}
        Component={() => (
          <GenericModalWrapper>
            {accessDenied ? (
              <AccessDeniedBox>
                <AccessDeniedTitle>Elevated access required</AccessDeniedTitle>
                <AccessDeniedText>
                  You do not have sufficient permissions to view or reset MFA
                  status for this user. Contact an administrator with the
                  appropriate role to perform this action.
                </AccessDeniedText>
              </AccessDeniedBox>
            ) : (
              <>
                <StatusRow>
                  <StatusLabel>Multifactor authentication:</StatusLabel>
                  <StatusBadge level={mfaLevel}>{badgeLabel}</StatusBadge>
                </StatusRow>

                {mfaLevel !== 'off' && (
                  <RemoveSection>
                    {!showConfirm ? (
                      <Button
                        btnStyle="secondary"
                        label="Remove 2FA"
                        onClick={() => setShowConfirm(true)}
                      />
                    ) : (
                      <WarningBox>
                        <WarningTitle>Warning: This action cannot be undone</WarningTitle>
                        <WarningText>
                          You are about to permanently remove multifactor
                          authentication for <strong>{userName}</strong>. This
                          will reduce the security of their account and they will
                          no longer be required to use a second factor when
                          logging in. Are you absolutely sure you want to
                          proceed?
                        </WarningText>
                        <ButtonRow>
                          <Button
                            btnStyle="primary"
                            label={removingMfa ? 'Removing...' : 'Yes, remove 2FA'}
                            onClick={handleConfirmRemove}
                            disabled={removingMfa}
                          />
                          <Button
                            btnStyle="secondary"
                            label="Cancel"
                            onClick={() => setShowConfirm(false)}
                            disabled={removingMfa}
                          />
                        </ButtonRow>
                      </WarningBox>
                    )}
                  </RemoveSection>
                )}
              </>
            )}
          </GenericModalWrapper>
        )}
      />
    );
  }
);

export default UserMfaModal;
